import { v4 as uuidv4 } from 'uuid'; // Optional: for more robust unique IDs

import { API_BASE_URL, DEFAULT_HEADERS } from '../../apiConfig';
import { resetClassroomState } from '../../redux/classroomSlice'; // You must implement this reducer if not already

// ✅ Utility function to   clean Markdown formatting
function cleanMarkdown(text) {
  const cleaned = text ? text.replace(/###/g, '').replace(/\*\*/g, '').trim() : '';

  return cleaned;
}

function normalizeLeader(value, fallback = 'Professor') {
  const raw = String(value || fallback).toLowerCase();
  if (raw.includes('librarian')) return 'Librarian';
  if (raw.includes('examiner')) return 'Examiner';
  return 'Professor';
}

function toDeepDiveActivities(indexItems = []) {
  return indexItems.map((item, position) => {
    const deepDive = item?.deepDive || item?.detail || {};
    const keyQuestions = Array.isArray(deepDive?.keyQuestions)
      ? deepDive.keyQuestions
      : Array.isArray(item?.keyQuestions)
        ? item.keyQuestions
        : [];
    const deliverables = Array.isArray(deepDive?.deliverables)
      ? deepDive.deliverables
      : Array.isArray(item?.deliverables)
        ? item.deliverables
        : [];
    const learningGoals = Array.isArray(deepDive?.learningGoals)
      ? deepDive.learningGoals
      : Array.isArray(item?.learningGoals)
        ? item.learningGoals
        : [];

    return {
      id: item?.id || item?.indexId || item?.nodeId || uuidv4(),
      title: cleanMarkdown(item?.title || `Deep Dive ${position + 1}`),
      leader: normalizeLeader(item?.leader || deepDive?.leader || 'Professor'),
      objective: cleanMarkdown(
        item?.objective ||
          deepDive?.summary ||
          learningGoals[0] ||
          'Deepen conceptual understanding'
      ),
      assessment: cleanMarkdown(deliverables[0] || ''),
      contentSeed: {
        summary: cleanMarkdown(deepDive?.summary || ''),
        keyQuestions,
        deliverables,
        learningGoals,
        references: Array.isArray(deepDive?.references) ? deepDive.references : [],
      },
    };
  });
}

// ✅ Parse and structure lesson data
export function parseLessonData(lessonPlan) {
  const root = lessonPlan?.lessonPlan || lessonPlan?.lessonIndexPlan || lessonPlan;
  if (!root) return null;

  // Backward-compatible path (legacy activities payload)
  if (root?.lessonMetadata && Array.isArray(root?.activities)) {
    return {
      lessonMetadata: {
        title: cleanMarkdown(root.lessonMetadata.title),
        objective: cleanMarkdown(root.lessonMetadata.objective),
      },
      segments: root.activities.map((activity, index) => {
        const id = activity.id || uuidv4();
        const title = cleanMarkdown(activity.title);
        return {
          id,
          sequence: index + 1,
          leader: normalizeLeader(activity.leader),
          title,
          objective: cleanMarkdown(activity.objective || ''),
          assessment: cleanMarkdown(activity.assessment || ''),
          messages: [],
          contentSeed: activity.contentSeed || {},
        };
      }),
    };
  }

  // Knowledge-graph/deep-dive path
  const indexItems =
    root?.lessonIndex ||
    root?.indices ||
    root?.sections ||
    root?.activities ||
    root?.deepDivePlan ||
    [];
  const deepDiveActivities = toDeepDiveActivities(indexItems);
  const parsed = {
    lessonMetadata: {
      title: cleanMarkdown(root?.title || root?.lessonTitle || 'Lesson Deep Dive'),
      objective: cleanMarkdown(
        root?.objective || root?.lessonObjective || root?.summary || 'Deep conceptual lesson'
      ),
    },
    segments: deepDiveActivities.map((activity, index) => {
      return {
        id: activity.id,
        sequence: index + 1,
        leader: activity.leader,
        title: activity.title,
        objective: activity.objective,
        assessment: activity.assessment,
        contentSeed: activity.contentSeed,
        messages: [],
      };
    }),
  };
  return parsed;
}

export const ClassroomService = {
  /**
   * Generate a lesson plan using unified lessonData from Classroom.js
   * @param {Object} lessonData - Structured payload from Classroom.js
   */
  /**
   * Generate a lesson plan using unified lessonData from Classroom.js
   * @param {Object} lessonData - Structured payload from Classroom.js
   */
  async generateLessonPlan(lessonData) {
    try {
      const payload = {
        mode: 'DEEP_DIVE_LESSON_INDEX',
        course: {
          id: lessonData?.courseId || null,
          title: lessonData?.title ?? 'Untitled Course',
          description: lessonData?.description ?? 'No course description provided.',
          difficultyLevel: lessonData?.difficultyLevel ?? 'Unknown',
          objectives: Array.isArray(lessonData?.courseObjectives)
            ? lessonData.courseObjectives
            : [],
          knowledgeGraph: Array.isArray(lessonData?.knowledgeGraph)
            ? lessonData.knowledgeGraph
            : [],
        },
        lesson: {
          id: lessonData?.lessonId || null,
          title: lessonData?.lessonTitle ?? 'Untitled Lesson',
          objective: lessonData?.lessonObjective ?? 'No lesson objective provided.',
          kgTopicId: lessonData?.kgTopicId || null,
          deepDivePrompt: lessonData?.deepDivePrompt || '',
        },
        learner: {
          name: lessonData?.profileName ?? 'Anonymous',
          educationLevel: lessonData?.educationLevel ?? 'Not specified',
          careerGoals: lessonData?.careerGoals ?? 'Not specified',
          interests: lessonData?.interests ?? 'Not specified',
          certifications: lessonData?.certifications ?? 'None',
        },
      };
      const legacyPayload = {
        courseTitle: lessonData?.title ?? 'Untitled Course',
        lessonTitle: lessonData?.lessonTitle ?? 'Untitled Lesson',
        lessonObjective: lessonData?.lessonObjective ?? '',
        learner: payload.learner,
      };

      const tryRequest = async (url, body) =>
        fetch(`${API_BASE_URL}${url}`, {
          method: 'POST',
          headers: DEFAULT_HEADERS,
          body: JSON.stringify(body),
        });

      let response = await tryRequest('/generate-lesson-index-plan', payload);
      if (!response.ok) {
        response = await tryRequest('/generate-lesson', legacyPayload);
      }
      if (!response.ok) {
        response = await tryRequest('/generate-lesson', lessonData);
      }
      if (!response.ok) {
        const errPayload = await response.json().catch(() => null);
        throw new Error(errPayload?.error || `HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      const parsed = parseLessonData(data.lessonPlan || data.lessonIndexPlan || data);
      if (parsed?.segments?.length) return parsed;

      // Last-resort local fallback to keep classroom usable.
      return {
        lessonMetadata: {
          title: lessonData?.lessonTitle || 'Lesson',
          objective: lessonData?.lessonObjective || 'Continue learning',
        },
        segments: [
          {
            id: uuidv4(),
            sequence: 1,
            leader: 'Professor',
            title: lessonData?.lessonTitle || 'Lesson Overview',
            objective: lessonData?.lessonObjective || 'Introduce core topic',
            assessment: '',
            contentSeed: {
              summary:
                lessonData?.deepDivePrompt ||
                'Walk through the topic fundamentals and connect to objectives.',
              keyQuestions: [],
              deliverables: [],
              learningGoals: [],
              references: [],
            },
            messages: [],
          },
        ],
      };
    } catch (error) {
      return null;
    }
  },

  // ✅ Evict from both localStorage and Redux state
  clearLessonFromCache(title, dispatch) {
    dispatch(resetClassroomState()); // You must define this reducer in classroomSlice
  },
};
